import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNoIndex } from '@/hooks/useSEO';

const SUPABASE_FUNCTIONS_URL = 'https://opocfbvkglexckmxhwsv.supabase.co/functions/v1';

interface Order {
  id: string;
  created: number;
  amount_total: number | null;
  currency: string;
  payment_status: string;
  metadata: Record<string, string>;
  receipt_url: string | null;
}

interface BlueprintDownloadState {
  status: 'idle' | 'loading' | 'success' | 'error';
  blueprints: Array<{ name: string; url: string; filename: string }>;
  error: string;
}

const BlueprintDownloadButton = ({
  orderId,
  customerEmail,
}: {
  orderId: string;
  customerEmail: string;
}) => {
  const [state, setState] = useState<BlueprintDownloadState>({
    status: 'idle',
    blueprints: [],
    error: '',
  });

  const handleDownload = async () => {
    if (state.status === 'loading') return;
    setState({ status: 'loading', blueprints: [], error: '' });

    try {
      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/get-blueprint-download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: orderId, email: customerEmail }),
      });
      const data = await res.json();

      if (data.error) {
        // Log debug info als beschikbaar
        if (data.debug_items || data.debug_metadata) {
          console.warn('[Blueprint Debug] Items:', JSON.stringify(data.debug_items));
          console.warn('[Blueprint Debug] Metadata:', JSON.stringify(data.debug_metadata));
        }
        setState({ status: 'error', blueprints: [], error: data.error });
        return;
      }

      const bps: Array<{ name: string; url: string; filename: string }> = data.blueprints ?? [];
      setState({ status: 'success', blueprints: bps, error: '' });

      // Direct openen in nieuw tabblad
      bps.forEach((bp) => {
        window.open(bp.url, '_blank', 'noopener,noreferrer');
      });
    } catch {
      setState({ status: 'error', blueprints: [], error: 'Er is iets misgegaan. Probeer opnieuw.' });
    }
  };

  if (state.status === 'success') {
    return (
      <div className="flex flex-col items-end gap-1.5">
        {state.blueprints.map((bp) => (
          <a
            key={bp.url}
            href={bp.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600/20 border border-emerald-500/40 text-emerald-400 text-xs font-medium rounded-lg hover:bg-emerald-600/30 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-download-line text-xs" />
            Download PDF
          </a>
        ))}
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="flex flex-col items-end gap-1">
        <button
          onClick={handleDownload}
          className="flex items-center gap-1.5 px-3 py-2 bg-rose-600/15 border border-rose-500/30 text-rose-400 text-xs font-medium rounded-lg hover:bg-rose-600/25 transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line text-xs" />
          Opnieuw
        </button>
        <span className="text-rose-400/70 text-xs max-w-[140px] text-right">{state.error}</span>
      </div>
    );
  }

  return (
    <button
      onClick={handleDownload}
      disabled={state.status === 'loading'}
      className="flex items-center gap-1.5 px-3 py-2 bg-fuchsia-600/15 border border-fuchsia-500/25 text-fuchsia-400 text-xs font-medium rounded-lg hover:bg-fuchsia-600/25 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
    >
      {state.status === 'loading' ? (
        <>
          <i className="ri-loader-4-line animate-spin text-xs" />
          Laden...
        </>
      ) : (
        <>
          <i className="ri-download-line text-xs" />
          Download
        </>
      )}
    </button>
  );
};

const typeLabel = (meta: Record<string, string>): string => {
  if (meta?.type === 'blueprint') return 'Ritueel Blueprint';
  if (meta?.type === 'consultation') return 'Spiritueel Consult';
  return 'Aankoop';
};

const typeIcon = (meta: Record<string, string>): string => {
  if (meta?.type === 'blueprint') return 'ri-file-download-line';
  if (meta?.type === 'consultation') return 'ri-calendar-check-line';
  return 'ri-shopping-bag-line';
};

const typeColor = (meta: Record<string, string>): string => {
  if (meta?.type === 'blueprint') return 'text-fuchsia-400 bg-fuchsia-500/15 border-fuchsia-500/25';
  if (meta?.type === 'consultation') return 'text-violet-400 bg-violet-500/15 border-violet-500/25';
  return 'text-white/60 bg-white/5 border-white/10';
};

const formatDate = (ts: number): string => {
  return new Date(ts * 1000).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const formatPrice = (cents: number | null, currency: string): string => {
  if (cents === null) return '—';
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
};

const OrderCard = ({ order, customerEmail }: { order: Order; customerEmail: string }) => {
  const label = typeLabel(order.metadata);
  const icon = typeIcon(order.metadata);
  const colorClass = typeColor(order.metadata);
  const productName =
    order.metadata?.blueprint_title ||
    order.metadata?.productName ||
    (order.metadata?.consultation_type ? `Consult – ${order.metadata.consultation_type}` : null) ||
    label;

  return (
    <div className="bg-[#110020]/70 border border-violet-500/15 rounded-2xl p-6 hover:border-violet-500/30 transition-all duration-300">
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        {/* Icon */}
        <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-violet-600/15 border border-violet-500/20 shrink-0">
          <i className={`${icon} text-xl text-violet-400`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${colorClass}`}>
              {label}
            </span>
            <span className="text-white/25 text-xs">#{order.id.slice(-8).toUpperCase()}</span>
          </div>

          <h3 className="text-white font-semibold text-base mb-1 truncate">{productName}</h3>

          <div className="flex flex-wrap items-center gap-4 mt-2">
            <div className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-violet-400/60 text-xs" />
              <span className="text-white/40 text-xs">{formatDate(order.created)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <i className="ri-shield-check-line text-emerald-400/60 text-xs" />
              <span className="text-emerald-400/80 text-xs font-medium">Betaald</span>
            </div>
          </div>

          {/* Consult metadata */}
          {order.metadata?.type === 'consultation' && (
            <div className="mt-3 p-3 bg-white/3 rounded-xl border border-white/5">
              <p className="text-white/35 text-xs">
                <span className="text-white/55">Naam:</span>{' '}
                {order.metadata.first_name} {order.metadata.last_name}
              </p>
              {order.metadata.date && (
                <p className="text-white/35 text-xs mt-0.5">
                  <span className="text-white/55">Gewenste datum:</span> {order.metadata.date}
                </p>
              )}
              {order.metadata.time_preference && (
                <p className="text-white/35 text-xs mt-0.5">
                  <span className="text-white/55">Tijdvoorkeur:</span> {order.metadata.time_preference}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Price + Actions */}
        <div className="flex flex-col items-end gap-3 shrink-0">
          <span className="text-white font-black text-xl">
            {formatPrice(order.amount_total, order.currency)}
          </span>
          <div className="flex items-center gap-2">
            {order.receipt_url && (
              <a
                href={order.receipt_url}
                target="_blank"
                rel="nofollow noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 bg-white/5 border border-white/10 text-white/60 text-xs font-medium rounded-lg hover:border-violet-500/40 hover:text-violet-400 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-receipt-line text-xs" />
                Bon
              </a>
            )}
            {order.metadata?.type === 'blueprint' && (
              <BlueprintDownloadButton
                orderId={order.id}
                customerEmail={customerEmail}
              />
            )}
            {order.metadata?.type === 'consultation' && (
              <Link
                to="/contact"
                className="flex items-center gap-1.5 px-3 py-2 bg-violet-600/15 border border-violet-500/25 text-violet-400 text-xs font-medium rounded-lg hover:bg-violet-600/25 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-message-3-line text-xs" />
                Contact
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const OrdersPage = () => {
  useNoIndex();
  const [email, setEmail] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    setOrders(null);
    setErrorMsg('');
    setSubmittedEmail(email.trim());

    try {
      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/get-orders-by-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (data.error) {
        setErrorMsg(data.error);
        setStatus('error');
      } else {
        setOrders(data.orders ?? []);
        setStatus('success');
      }
    } catch {
      setErrorMsg('Er is iets misgegaan. Probeer het opnieuw.');
      setStatus('error');
    }
  };

  const handleReset = () => {
    setEmail('');
    setSubmittedEmail('');
    setOrders(null);
    setStatus('idle');
    setErrorMsg('');
  };

  return (
    <div className="min-h-screen bg-[#050010] pt-20">
      {/* Hero */}
      <div className="relative py-20 text-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/4251de76d0b236049ab5bfb01e623974.jpg"
            alt="Mijn Bestellingen"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050010]/80 via-[#050010]/65 to-[#050010]" />
        </div>
        <div className="relative z-10 px-6">
          <p className="text-violet-400 text-xs font-semibold uppercase tracking-widest mb-3">✦ Jouw Account ✦</p>
          <h1 className="text-5xl font-black text-white mb-4">
            Mijn <span className="text-transparent bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text">Bestellingen</span>
          </h1>
          <p className="text-white/50 text-base max-w-xl mx-auto">
            Vul je e-mailadres in om al je aankopen, blueprints en consulten terug te vinden.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 lg:px-12 pb-24">

        {/* Search Card */}
        <div className="bg-[#110020]/70 border border-violet-500/20 rounded-3xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-violet-600/20">
              <i className="ri-search-line text-violet-400 text-lg" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">Bestellingen Zoeken</h2>
              <p className="text-white/35 text-xs">Gebruik het e-mailadres waarmee je hebt betaald</p>
            </div>
          </div>

          {status === 'success' ? (
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-violet-500/20">
                  <i className="ri-mail-check-line text-violet-400 text-sm" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{submittedEmail}</p>
                  <p className="text-white/35 text-xs">{orders?.length ?? 0} bestelling{(orders?.length ?? 0) !== 1 ? 'en' : ''} gevonden</p>
                </div>
              </div>
              <button
                onClick={handleReset}
                className="flex items-center gap-1.5 px-4 py-2 border border-violet-500/25 text-violet-400 text-sm rounded-xl hover:bg-violet-500/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line text-xs" />
                Ander e-mail
              </button>
            </div>
          ) : (
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
                  <i className="ri-mail-line text-violet-400/60 text-base" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  placeholder="jij@email.nl"
                  className="w-full pl-11 pr-4 py-3.5 bg-white/5 border border-violet-500/20 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:border-violet-500/50 transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={status === 'loading' || !email.trim()}
                className="flex items-center justify-center gap-2 px-7 py-3.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold text-sm rounded-xl whitespace-nowrap hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {status === 'loading' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-sm" />
                    Zoeken...
                  </>
                ) : (
                  <>
                    <i className="ri-search-line text-sm" />
                    Zoeken
                  </>
                )}
              </button>
            </form>
          )}

          {status === 'error' && (
            <p className="mt-3 text-rose-400 text-sm flex items-center gap-2">
              <i className="ri-error-warning-line" />
              {errorMsg}
            </p>
          )}
        </div>

        {/* Results */}
        {status === 'success' && orders !== null && (
          <div>
            {orders.length === 0 ? (
              /* Empty State */
              <div className="text-center py-16 bg-[#110020]/40 border border-violet-500/10 rounded-3xl">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-violet-600/10 border border-violet-500/15 mx-auto mb-5">
                  <i className="ri-inbox-line text-3xl text-violet-400/40" />
                </div>
                <h3 className="text-white/60 font-semibold text-lg mb-2">Geen bestellingen gevonden</h3>
                <p className="text-white/30 text-sm max-w-xs mx-auto mb-6">
                  We konden geen bestellingen vinden voor <strong className="text-white/50">{submittedEmail}</strong>.
                  Controleer of je het juiste e-mailadres hebt ingevoerd.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/consultations"
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold rounded-xl whitespace-nowrap hover:opacity-90 transition-opacity cursor-pointer"
                  >
                    <i className="ri-calendar-line" />
                    Consult Boeken
                  </Link>
                  <Link
                    to="/consultations?tab=blueprints"
                    className="flex items-center gap-2 px-6 py-3 border border-violet-500/25 text-violet-400 text-sm font-medium rounded-xl whitespace-nowrap hover:bg-violet-500/10 transition-colors cursor-pointer"
                  >
                    <i className="ri-file-download-line" />
                    Blueprints Kopen
                  </Link>
                </div>
              </div>
            ) : (
              <>
                {/* Order list */}
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-white font-bold text-lg">
                    Jouw Bestellingen
                    <span className="ml-2 text-sm font-normal text-white/30">({orders.length})</span>
                  </h2>
                  <div className="flex items-center gap-1.5 text-emerald-400/70 text-xs">
                    <i className="ri-shield-check-line" />
                    Geverifieerd via Stripe
                  </div>
                </div>

                <div className="space-y-4">
                  {orders
                    .sort((a, b) => b.created - a.created)
                    .map(order => (
                      <OrderCard key={order.id} order={order} customerEmail={submittedEmail} />
                    ))}
                </div>

                {/* Total spend */}
                <div className="mt-6 p-5 bg-violet-600/8 border border-violet-500/15 rounded-2xl flex items-center justify-between">
                  <div>
                    <p className="text-white/40 text-xs mb-0.5">Totaal besteed</p>
                    <p className="text-white font-black text-xl">
                      {formatPrice(
                        orders.reduce((sum, o) => sum + (o.amount_total ?? 0), 0),
                        orders[0]?.currency ?? 'eur'
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-white/40 text-xs mb-0.5">Bestellingen</p>
                    <p className="text-white font-black text-xl">{orders.length}</p>
                  </div>
                </div>

                {/* Help Block */}
                <div className="mt-6 p-5 bg-[#1a0030]/50 border border-violet-500/10 rounded-2xl flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-violet-600/15 shrink-0">
                    <i className="ri-question-line text-violet-400 text-base" />
                  </div>
                  <div>
                    <p className="text-white/60 font-medium text-sm mb-1">Vragen over je bestelling?</p>
                    <p className="text-white/35 text-xs leading-relaxed mb-3">
                      Heb je je downloadlink niet ontvangen of wil je een afspraak inplannen? Neem gerust contact op.
                    </p>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-1.5 text-violet-400 text-xs font-medium hover:text-violet-300 transition-colors cursor-pointer"
                    >
                      <i className="ri-mail-send-line text-xs" />
                      Stuur een bericht
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Idle state info blocks */}
        {status === 'idle' && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            {[
              {
                icon: 'ri-file-download-line',
                color: 'text-fuchsia-400 bg-fuchsia-600/10 border-fuchsia-500/15',
                title: 'Ritueel Blueprints',
                desc: 'Vind al je gekochte digitale gidsen en download ze opnieuw.',
              },
              {
                icon: 'ri-calendar-check-line',
                color: 'text-violet-400 bg-violet-600/10 border-violet-500/15',
                title: 'Spirituele Consulten',
                desc: 'Bekijk details van je geboekte consulten en afspraakgegevens.',
              },
              {
                icon: 'ri-receipt-line',
                color: 'text-white/60 bg-white/5 border-white/10',
                title: 'Betaalbewijzen',
                desc: 'Download je officiële Stripe betaalbon voor administratie.',
              },
            ].map(item => (
              <div key={item.title} className={`p-5 rounded-2xl border ${item.color} bg-opacity-50`}>
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl mb-3 ${item.color}`}>
                  <i className={`${item.icon} text-lg`} />
                </div>
                <h3 className="text-white/80 font-semibold text-sm mb-1">{item.title}</h3>
                <p className="text-white/35 text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
